/**
 * A REACTION AS A DOCUMENT, SO THE EDITOR CAN BEHAVE LIKE THE FORMULA ONE.
 *
 * ── WHY THE TOKEN ROW IS BEING REPLACED RATHER THAN REPAIRED ─────────────────────────────────
 * The row rendered every token except the one under the caret, which was a plain `<input>`
 * showing text. Three bugs in a row came out of that shape rather than out of any single
 * mistake: a caret React could not see, so controls that depend on it never updated; offsets
 * meaning different things either side of the display conversion; and an empty token that was
 * real editor state yet wrote nothing out, so a history comparing sources discarded it.
 *
 * All three stop existing if the document is a document. ProseMirror owns the caret, the
 * selection, the history and the node boundaries, and it is already a dependency used exactly
 * this way a few files away.
 *
 * ── WHAT THE TEACHER SEES WHILE TYPING IS NOT KATEX, AND THAT IS ALLOWED ─────────────────────
 * The precedent is the formula editor: MathLive draws it while you type, KaTeX draws it on the
 * page, and nobody has ever noticed the difference. So the live styling here is CSS over the
 * document — digits after an element set as subscripts, states in italic — and KaTeX stays the
 * renderer of record for what is committed.
 *
 * ── THE SHAPE ─────────────────────────────────────────────────────────────────────────────────
 *   text          plain chemistry, carrying colour and strike as MARKS
 *   rsym          an inserted symbol: one atom, one glyph, never half-typed
 *   rframe        a frame with real holes — an arrow's label, a nuclide's slots
 *   rstructure    a drawn molecule
 *
 * Spaces are just text. The token boundary that mattered so much to the row is derived at
 * serialisation, where mhchem needs it, and nowhere else.
 */

import { readCoSubstrate, writeCoSubstrate } from './reaction-cosubstrate';
import { toDisplay, toDisplayMapped, toSource } from './reaction-display';
import { lewisValue, parseLewisValue, readLewis, writeLewis } from './reaction-lewis';
import { ARROW_CODES, CATEGORIES, DRAWN_CODES, GLYPH_FOR_CODE } from './reaction-symbols';
import { structureFields, writeStructureToken } from './reaction-tokens';

export type DocMark =
  | { type: 'rcolor'; attrs: { value: string } }
  | { type: 'rstrike' }
  | { type: 'ritalic' }
  /**
   * LEWIS DOTS, which this was a lone pair above or below until it grew two more sides.
   *
   * `value` is four digits, clockwise from the top — see `reaction-lewis`, which owns both the
   * encoding and the spelling so that this layer and the token layer cannot disagree.
   */
  | { type: 'rlewis'; attrs: { value: string } }
  | { type: 'roxidation'; attrs: { value: string } }
  /** The antiparticle bar: p becomes p-bar. No value — a bar is a bar. */
  | { type: 'rbar' }
  /** Words under a species, which is the one mark here whose content is prose. */
  | { type: 'rannotation'; attrs: { value: string } };

export type DocNode =
  | { type: 'text'; text: string; marks?: DocMark[] }
  /**
   * An inserted symbol, which CAN carry marks.
   *
   * It could not until a teacher struck an arrow through to mean "does not react" — the scope's
   * own example of something that comes free — and found it gone on reopening: the wrap was
   * read, the atom inside it was kept and the mark on the way in was dropped on the floor.
   */
  | { type: 'rsym'; attrs: { code: string }; marks?: DocMark[] }
  | { type: 'rstructure'; attrs: { smiles: string; name: string; arrows?: string; annotations?: string }; marks?: DocMark[] }
  | { type: 'rframe'; attrs: { kind: FrameKind; arrow?: string }; content: DocNode[][] };

export type FrameKind =
  | 'arrowLabel' | 'arrowBelow' | 'arrowBoth' | 'arrowCoSubstrate' | 'nuclide'
  | 'lonePairAbove' | 'lonePairBelow' | 'repeatUnit' | 'repeatBracket';

/**
 * THE ARROW A FRAME WRAPS, which used to be `->` and nothing else.
 *
 * mhchem will label any of them — `<=>[cat]`, `<-->[x]`, `<=>>[][below]` all render — and the
 * frames simply never asked, so a catalysed equilibrium could not be authored at all: Haber and
 * the Contact process were both unwritable in an editor built for school chemistry.
 */
export const DEFAULT_FRAME_ARROW = '->';

/** The arrow of a frame that has one, defaulted for every frame written before frames had one. */
export const frameArrow = (attrs: { arrow?: string }): string =>
  attrs.arrow || DEFAULT_FRAME_ARROW;

/**
 * How each frame writes itself out, given its filled slots.
 *
 * A NUCLIDE CARRIES ITS ELEMENT. It was two holes and nothing else, so a teacher who opened one
 * had a mass number and an atomic number and nowhere to put the U — and the symbol typed after
 * the frame sat outside it, which is not what a nuclide is. Three slots, and the third is the
 * element.
 */
const FRAMES: Record<FrameKind, {
  slots: number;
  write: (s: string[], arrow: string) => string;
  /** Carries an `arrow` attribute: the arrow it wraps is the teacher's, not a fixed `->`. */
  arrow?: true;
  /**
   * One of the three the decoration popup offers.
   *
   * NOT every arrow frame is. The co-substrate arc carries an arrow like the others and is a
   * KEY rather than a decoration — offering it in a popup of "where does the label go" would
   * answer a different question from the one that popup asks.
   */
  decoration?: true;
}> = {
  arrowLabel: { slots: 1, arrow: true, decoration: true, write: ([a], arr) => `${arr}[${a ?? ''}]` },
  /* mhchem's way of writing a label UNDER an arrow is an empty bracket above it, so a
     below-only arrow and a both-arrow with nothing on top are the same string. They are
     therefore the same thing, and this is the one that reads it back — see FRAME_PATTERNS. */
  arrowBelow: { slots: 1, arrow: true, decoration: true, write: ([b], arr) => `${arr}[][${b ?? ''}]` },
  arrowBoth: { slots: 2, arrow: true, decoration: true, write: ([a, b], arr) => `${arr}[${a ?? ''}][${b ?? ''}]` },
  /*
   * The co-substrate arc. Two slots like the both-arrow, because it carries the same two
   * species — what joins and what leaves — and a different drawing is the whole difference.
   * Written as Agavi's own syntax, since mhchem has no arc: see `reaction-cosubstrate`.
   */
  arrowCoSubstrate: {
    slots: 2,
    arrow: true,
    write: ([a, b], arr) => writeCoSubstrate({ arrow: arr, into: a ?? '', out: b ?? '' }),
  },
  nuclide: { slots: 3, write: ([a, b, c]) => `^{${a ?? ''}}_{${b ?? ''}}${c ?? ''}` },
  lonePairAbove: { slots: 1, write: ([a]) => `$\\overset{..}{${a ?? ''}}$` },
  lonePairBelow: { slots: 1, write: ([a]) => `$\\underset{..}{${a ?? ''}}$` },
  repeatUnit: { slots: 1, write: ([a]) => `(${a ?? ''})_n` },
  repeatBracket: { slots: 1, write: ([a]) => `-[${a ?? ''}]-` },
};

/** Whether a frame wraps an arrow of the teacher's choosing rather than a fixed one. */
export const framesAnArrow = (kind: FrameKind): boolean => !!FRAMES[kind].arrow;

/** The ways an arrow can be DECORATED — which is not every frame that holds an arrow. */
export const ARROW_FRAME_KINDS =
  (Object.keys(FRAMES) as FrameKind[]).filter(k => FRAMES[k].decoration);

/** How many holes a frame has, which the editor needs before it has built one. */
export const frameSlots = (kind: FrameKind): number => FRAMES[kind].slots;

/** Every frame there is, so a test can walk them rather than restate their shapes. */
export const FRAME_KINDS = Object.keys(FRAMES) as FrameKind[];

/** A frame written out with the given slots filled. Exported for the render invariant: a test
 *  that retyped these shapes would pin the copy rather than the table. */
export const writeFrame = (kind: FrameKind, slots: string[], arrow = DEFAULT_FRAME_ARROW): string =>
  FRAMES[kind].write(slots, arrow);

/** The frame a palette code opens, when it opens one. */
export function frameFor(code: string): FrameKind | null {
  const hit = docFromSource(code);
  return hit.length === 1 && hit[0]!.type === 'rframe' ? hit[0]!.attrs.kind : null;
}

// ── Document → mhchem ─────────────────────────────────────────────────────────

const wrapMarks = (text: string, marks: DocMark[] | undefined): string => {
  let out = text;
  // Innermost first, so colour ends up outside — the order the renderer accepts.
  /*
   * Italic takes NO inner `\ce{}`, where every other wrap here does.
   * Measured: `$\mathit{\ce{cis}}$` renders UPRIGHT, because `\ce` sets upright and it is the
   * inner one. The others keep `\ce` to keep their subscripts; italic has to drop it to be
   * italic at all, which means a digit inside an italic run is no longer subscripted. That is
   * the right trade — what italic is FOR here is `cis`, `trans`, `n`- and a variable like the x
   * in Fe_xO_y, none of which have a subscript to lose.
   */
  /*
   * THE BAR IS INNERMOST, because it belongs to the species itself: a struck antiproton is a
   * barred p with a line through it, never a bar drawn over a struck p.
   */
  if (marks?.some(m => m.type === 'rbar')) out = `$\\bar{\\ce{${out}}}$`;
  if (marks?.some(m => m.type === 'ritalic')) out = `$\\mathit{${out}}$`;
  /*
   * Lewis dots: the same `\overset` an oxidation number uses, which is why they read back
   * ambiguously and why a group of one or two dots is reserved. A teacher cannot type `..` as
   * an oxidation number — the popup offers a fixed list of states — so it is free to mean this.
   */
  const lewis = marks?.find((m): m is Extract<DocMark, { type: 'rlewis' }> => m.type === 'rlewis');
  if (lewis) out = writeLewis(out, parseLewisValue(lewis.attrs.value));
  const ox = marks?.find((m): m is Extract<DocMark, { type: 'roxidation' }> => m.type === 'roxidation');
  if (ox) out = `$\\overset{${ox.attrs.value}}{\\ce{${out}}}$`;
  /*
   * WORDS UNDER A SPECIES, and `\text{}` rather than `\ce{}` around them.
   *
   * `\underset{calcium carbonate}{\ce{CaCO3}}` renders, which is the trap: inside a reaction
   * mhchem reads Ca, C and O as ELEMENTS and sets the digits low, so the gloss that was meant
   * to make the formula readable comes out as more chemistry.
   */
  const note = marks?.find(
    (m): m is Extract<DocMark, { type: 'rannotation' }> => m.type === 'rannotation');
  if (note) out = `$\\underset{\\text{${note.attrs.value}}}{\\ce{${out}}}$`;
  if (marks?.some(m => m.type === 'rstrike')) out = `$\\cancel{\\ce{${out}}}$`;
  const colour = marks?.find((m): m is Extract<DocMark, { type: 'rcolor' }> => m.type === 'rcolor');
  if (colour) out = `[${colour.attrs.value}:${out}]`;
  return out;
};

/**
 * A COLOURED SUBSCRIPT STAYS A SUBSCRIPT, which the whole-token rule said was impossible.
 *
 * It said so because it was measured on `[a:CO][b:2]` — splitting one species into two coloured
 * runs, which really cannot keep the subscript by any route. A subscript on its own is a
 * different question, and `\ce{CO_\textcolor{red}{3}}` answers it: the vlist, the shrink and the
 * colour all survive, and it composes with arrows and the rest of the reaction.
 *
 * Only for a run that IS a subscript: digits alone, carrying colour alone, following something a
 * digit can subscript. Anything else goes back to wrapping the whole token.
 */
function subscriptColour(src: string, marks: DocMark[] | undefined, before: string): string | null {
  if (!marks || marks.length !== 1 || marks[0]!.type !== 'rcolor') return null;
  if (!/^\d+$/.test(src)) return null;
  if (!/[A-Za-z)\]]$/.test(before)) return null;
  return `_\\textcolor{${marks[0]!.attrs.value}}{${src}}`;
}

function nodeSource(node: DocNode, before = ''): string {
  switch (node.type) {
    case 'text': {
      const src = toSource(node.text);
      return subscriptColour(src, node.marks, before) ?? wrapMarks(src, node.marks);
    }
    case 'rsym': return wrapMarks(node.attrs.code, node.marks);
    case 'rstructure': {
      /* Through the token layer's own writer, so the two cannot spell this differently. */
      const drawn = writeStructureToken(node.attrs);
      /* A highlight wraps the WHOLE drawing — there is no range inside a picture to put one on,
         the same reason the structure is an atom here. */
      const colour = node.marks?.find(
        (m): m is Extract<DocMark, { type: 'rcolor' }> => m.type === 'rcolor');
      return colour ? `[${colour.attrs.value}:${drawn}]` : drawn;
    }
    case 'rframe': {
      const slots = node.content.map(slot => slot.reduce((acc, n) => acc + nodeSource(n, acc), ''));
      return FRAMES[node.attrs.kind].write(slots, frameArrow(node.attrs));
    }
  }
}

/**
 * `{}` WHERE A LaTeX CONTROL WORD WOULD OTHERWISE SWALLOW THE NEXT LETTER.
 *
 * `\dashrightarrow` followed straight by `Ca` is the single undefined command
 * `\dashrightarrowCa`, and the whole reaction fails to render — reported from the editor as
 * errors whenever a space was left out after an arrow. `\Delta`, `\alpha` and every other
 * escape in the palette have the same fault; the arrows were simply the ones a teacher butts
 * a species against.
 *
 * A control word ends at the first non-letter, so an empty group terminates it and prints
 * nothing. Applied to the FINISHED string rather than at the node boundaries, because the two
 * paths that produce this fault are different: an arrow is an atom and meets its species at a
 * boundary, while `\Delta` is display text and meets it INSIDE one text node, where there is
 * no boundary to act at. One sweep catches both.
 *
 * And only where a letter actually follows. A space ends a control word by itself, and `\ce{`,
 * `\text{` and every other wrapper is followed by a brace — so a reaction that already reads
 * correctly is not rewritten.
 */
/**
 * The palette codes that are a BARE control word, longest first.
 *
 * Named rather than matched by shape, because `\RightarrowCa` is genuinely ambiguous to a
 * pattern: `\[A-Za-z]+` followed by a letter backtracks and splits the word itself, which is
 * how the first attempt turned `\alpha{}Ca` into `\alph{}a{}Ca`. Only a list of the words
 * that exist can say where one ends.
 *
 * Derived from the palette, so a code added there is covered without a second edit. Codes that
 * already end in `{}` or a brace are not at risk and are not here.
 */
const CONTROL_WORDS = CATEGORIES
  .flatMap(c => c.items)
  .map(i => i.code)
  .filter(code => /^\\[A-Za-z]+$/.test(code))
  .sort((a, b) => b.length - a.length);

const RUNS_INTO_A_LETTER = new RegExp(
  `(${CONTROL_WORDS.map(w => w.replace(/[\\]/g, '\\\\')).join('|')})(?=[A-Za-z])`, 'g');

const terminateControlWords = (src: string): string =>
  (CONTROL_WORDS.length ? src.replace(RUNS_INTO_A_LETTER, '$1{}') : src);

/**
 * The whole document as mhchem.
 *
 * Runs of adjacent text with the SAME marks are joined before wrapping, or a colour applied
 * across a word would come out as one wrap per character.
 */
export function sourceFromDoc(nodes: DocNode[]): string {
  const merged: DocNode[] = [];
  for (const node of nodes) {
    const prev = merged[merged.length - 1];
    if (node.type === 'text' && prev?.type === 'text'
        && JSON.stringify(prev.marks ?? []) === JSON.stringify(node.marks ?? [])) {
      prev.text += node.text;
      continue;
    }
    merged.push(node.type === 'text' ? { ...node } : node);
  }
  /* Accumulated as it goes, because a subscript is only a subscript given what PRECEDES it. */
  return terminateControlWords(
    merged.reduce((acc, node) => acc + nodeSource(node, acc), ''));
}

// ── mhchem → document ─────────────────────────────────────────────────────────

/**
 * Longest and most specific first, so `->[a]` never matches inside `->[a][b]`.
 *
 * These are read on the way IN and are also what the palette inserts, because the modal now
 * routes every button through `docFromSource`. Before that, pressing the heated arrow typed the
 * literal text `->[Δ]` while REOPENING the same reaction drew a proper arrow with Δ over it —
 * insert and reload disagreed on every pre-filled frame in the palette.
 */
/**
 * ANY arrow, as one alternation.
 *
 * Built from the palette rather than written out, so an arrow added there can carry a label
 * here without a second edit.
 *
 * NOT sorted longest-first, which it was until a mutation showed the sort could not fail. `<=>`
 * is inside `<=>>` and `<-` is inside `<-->`, but what follows the arrow in every one of these
 * patterns is a BRACKET: the short alternative matches, the bracket does not, and the engine
 * backtracks to the long one. No arrow code contains a bracket, so the ambiguity a sort would
 * resolve cannot arise. The behaviour is pinned by a test either way.
 */
const ANY_ARROW = ARROW_CODES
  .map(code => code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  .join('|');

type FramePattern = {
  pattern: RegExp;
  kind: FrameKind;
  slots: number;
  /** An arrow frame captures the arrow as group 1; its slots follow. */
  carriesArrow?: true;
};

const FRAME_PATTERNS: FramePattern[] = [
  /* BEFORE the both-arrow, and requiring something below it. An empty bracket above is how
     mhchem writes a label underneath, so `->[][cat]` IS the below-only arrow and must read back
     as one rather than as a both-arrow with a vacant box over it. `->[][]` has nothing below
     either, so it falls through to the both-arrow it was inserted as. */
  { pattern: new RegExp(`^(${ANY_ARROW})\\[\\]\\[([^\\]]+)\\]`), kind: 'arrowBelow', slots: 1, carriesArrow: true },
  { pattern: new RegExp(`^(${ANY_ARROW})\\[([^\\]]*)\\]\\[([^\\]]*)\\]`), kind: 'arrowBoth', slots: 2, carriesArrow: true },
  { pattern: new RegExp(`^(${ANY_ARROW})\\[([^\\]]*)\\]`), kind: 'arrowLabel', slots: 1, carriesArrow: true },
  { pattern: /^\^\{([^}]*)\}_\{([^}]*)\}([A-Za-z]*)/, kind: 'nuclide', slots: 3 },
  { pattern: /^\$\\overset\{\.\.\}\{([^}]*)\}\$/, kind: 'lonePairAbove', slots: 1 },
  { pattern: /^\$\\underset\{\.\.\}\{([^}]*)\}\$/, kind: 'lonePairBelow', slots: 1 },
  { pattern: /^\(([^)]*)\)_n/, kind: 'repeatUnit', slots: 1 },
  { pattern: /^-\[([^\]]*)\]-/, kind: 'repeatBracket', slots: 1 },
];

/**
 * SYMBOLS THE DISPLAY TABLE REFUSES, HELD AS ATOMS INSTEAD.
 *
 * The table admits a rule only if the whole corpus still round-trips with it in, so a handful of
 * palette symbols have no display form and were showing their own source: the two lopsided
 * equilibria came out as `⇌>` and `<⇌` because the plain equilibrium matched inside them, the
 * gas and precipitate arrows showed `^` and `v`, and the negative charge showed `^-` — `e^-{}`
 * already owns that mapping, so admitting a bare one would turn `2e^-` into the electron.
 *
 * An atom cannot be split and serialises to exactly its code, so it can carry a glyph no table
 * would allow. The scan takes the LONGEST match at each position across both sets together,
 * which is what keeps `e^-{}` whole while `2OH^-` still gets its minus.
 */
/* Every drawn code, plus the one charge that has to stay whole. See `DRAWN_CODES`: a glyph
   made of SVG has no text form to fall back to, so it can only ever be one atom. */
const ATOM_CODES = [...DRAWN_CODES, '^-'];

/** Every palette code the table CAN show, so a longer text code beats a shorter atom one. */
const TEXT_CODES = CATEGORIES
  .filter(c => !c.elements)
  .flatMap(c => c.items)
  .filter(i => i.code.length > 1 && !ATOM_CODES.includes(i.code) && toDisplay(i.code) !== i.code)
  .map(i => i.code);

const SCAN: [string, boolean][] = [
  ...ATOM_CODES.map(c => [c, true] as [string, boolean]),
  ...TEXT_CODES.map(c => [c, false] as [string, boolean]),
].sort((a, b) => b[0].length - a[0].length);

/**
 * `^` and `v` standing alone, which is the only place they mean gas and precipitate.
 *
 * Everywhere else `^` opens a superscript and `v` is somebody's letter, which is why the display
 * table excludes both outright rather than testing them — no round-trip check catches a wrong
 * READING.
 */
function loneOperator(src: string, i: number): string | null {
  if (src[i] === '^' && !/[{+\-\\0-9]/.test(src[i + 1] ?? ' ')) return '^';
  if (src[i] === 'v' && !/[A-Za-z]/.test(src[i - 1] ?? ' ') && !/[A-Za-z]/.test(src[i + 1] ?? ' ')) return 'v';
  return null;
}

/* See `reaction-tokens`: the SMILES may hold square brackets, so the token's end is found by
   matching the bracket rather than by a pattern that forbids them. */
const STRUCTURE_OPEN = '[smiles:';
const COLOUR = /^\[(#[0-9A-Fa-f]{3,6}):/;
/** The coloured subscript above, on the way back in. */
const SUB_COLOUR = /^_\\textcolor\{(#[0-9A-Fa-f]{3,6})\}\{(\d+)\}/;
const STRIKE = '$\\cancel{\\ce{';
const OXIDATION = /^\$\\overset\{([^}]*)\}\{\\ce\{/;
const ITALIC = '$\\mathit{';
const BAR = '$\\bar{\\ce{';
const ANNOTATION_OPEN = '$\\underset{\\text{';
const ANNOTATION = /^\$\\underset\{\\text\{/;

/** The index just past the bracket or brace group opening at `from`. */
function closeOf(src: string, from: number, open: string, shut: string): number {
  let depth = 0;
  for (let i = from; i < src.length; i++) {
    if (src[i] === open) depth++;
    else if (src[i] === shut && --depth === 0) return i + 1;
  }
  return -1;
}

/**
 * Read mhchem into nodes.
 *
 * Anything unrecognised stays text, which is what keeps a half-typed bracket or a teacher's own
 * notation from being swallowed. Text is converted to its display form on the way in, the same
 * boundary the row used.
 */
export function docFromSource(source: string, marks: DocMark[] = []): DocNode[] {
  const out: DocNode[] = [];
  let plain = '';
  const flush = () => {
    if (!plain) return;
    out.push(marks.length ? { type: 'text', text: toDisplayMapped(plain).text, marks }
                          : { type: 'text', text: toDisplayMapped(plain).text });
    plain = '';
  };

  let i = 0;
  while (i < source.length) {
    const rest = source.slice(i);

    if (rest.startsWith(STRUCTURE_OPEN)) {
      const close = closeOf(source, i, '[', ']');
      if (close > 0) {
        flush();
        const { smiles, name, arrows, annotations } =
          structureFields(source.slice(i + STRUCTURE_OPEN.length, close - 1));
        const attrs = {
          smiles, name, ...(arrows ? { arrows } : {}), ...(annotations ? { annotations } : {}),
        };
        out.push(marks.length
          ? { type: 'rstructure', attrs, marks }
          : { type: 'rstructure', attrs });
        i = close;
        continue;
      }
    }

    const subColour = SUB_COLOUR.exec(rest);
    if (subColour) {
      flush();
      out.push({
        type: 'text',
        text: subColour[2]!,
        marks: [...marks, { type: 'rcolor', attrs: { value: subColour[1]! } }],
      });
      i += subColour[0].length;
      continue;
    }

    const colour = COLOUR.exec(rest);
    if (colour) {
      const close = closeOf(source, i, '[', ']');
      const inner = close > 0 ? source.slice(i + colour[0].length, close - 1) : '';
      if (close > 0 && inner) {
        flush();
        out.push(...docFromSource(inner, [...marks, { type: 'rcolor', attrs: { value: colour[1]! } }]));
        i = close;
        continue;
      }
    }

    if (rest.startsWith(STRIKE)) {
      const inner = closeOf(source, i + '$\\cancel{'.length + '\\ce'.length, '{', '}');
      const outer = closeOf(source, i + '$\\cancel'.length, '{', '}');
      if (inner > 0 && outer > 0 && source[outer] === '$') {
        flush();
        out.push(...docFromSource(source.slice(i + STRIKE.length, inner - 1),
                                  [...marks, { type: 'rstrike' }]));
        i = outer + 1;
        continue;
      }
    }

    if (rest.startsWith(ITALIC)) {
      const close = closeOf(source, i + '$\\mathit'.length, '{', '}');
      if (close > 0 && source[close] === '$') {
        flush();
        out.push(...docFromSource(source.slice(i + ITALIC.length, close - 1),
                                  [...marks, { type: 'ritalic' }]));
        i = close + 1;
        continue;
      }
    }

    if (rest.startsWith(BAR)) {
      const inner = closeOf(source, i + '$\\bar'.length + '{\\ce'.length, '{', '}');
      const outer = closeOf(source, i + '$\\bar'.length, '{', '}');
      if (inner > 0 && outer > 0 && source[outer] === '$') {
        flush();
        out.push(...docFromSource(source.slice(i + BAR.length, inner - 1),
                                  [...marks, { type: 'rbar' }]));
        i = outer + 1;
        continue;
      }
    }

    /*
     * Before the lone pair, whose own underset would otherwise claim this one's opening.
     *
     * Two brace groups in a row — `{\text{…}}{\ce{…}}` — and the second starts where the first
     * ENDS, not where its inner group ends. Getting that wrong does not throw: the branch
     * simply never matches and the whole wrap stays text, which round-trips perfectly and
     * carries no mark at all. A probe that only checked the round trip said it worked.
     */
    if (ANNOTATION.test(rest)) {
      const wordsAt = i + '$\\underset'.length;
      const words = closeOf(source, wordsAt + '{\\text'.length, '{', '}');
      const speciesAt = closeOf(source, wordsAt, '{', '}');
      if (words > 0 && speciesAt > 0 && source.slice(speciesAt).startsWith('{\\ce{')) {
        const inner = closeOf(source, speciesAt + '{\\ce'.length, '{', '}');
        const outer = closeOf(source, speciesAt, '{', '}');
        if (inner > 0 && outer > 0 && source[outer] === '$') {
          flush();
          const value = source.slice(i + ANNOTATION_OPEN.length, words - 1);
          out.push(...docFromSource(source.slice(speciesAt + '{\\ce{'.length, inner - 1),
                                    [...marks, { type: 'rannotation', attrs: { value } }]));
          i = outer + 1;
          continue;
        }
      }
    }

    /* The arc, before anything else looks at the bracket it opens with. */
    const arc = readCoSubstrate(source, i);
    if (arc) {
      flush();
      out.push({
        type: 'rframe',
        attrs: { kind: 'arrowCoSubstrate', arrow: arc.arrow },
        content: [docFromSource(arc.into), docFromSource(arc.out)],
      });
      i = arc.end;
      continue;
    }

    /* Read by the module that writes it, so the two can never drift apart. It takes the two
       shapes the lone-pair control used to write as the cases of itself that they are. */
    const lewis = readLewis(source, i);
    if (lewis) {
      flush();
      out.push(...docFromSource(lewis.inner,
                                [...marks, { type: 'rlewis', attrs: { value: lewisValue(lewis.dots) } }]));
      i = lewis.end;
      continue;
    }

    const ox = OXIDATION.exec(rest);
    if (ox) {
      const inner = closeOf(source, i + ox[0].length - '{\\ce{'.length + '{\\ce'.length, '{', '}');
      const outer = closeOf(source, i + '$\\overset'.length + `{${ox[1]}}`.length, '{', '}');
      if (inner > 0 && outer > 0 && source[outer] === '$') {
        flush();
        out.push(...docFromSource(source.slice(i + ox[0].length, inner - 1),
                                  [...marks, { type: 'roxidation', attrs: { value: ox[1]! } }]));
        i = outer + 1;
        continue;
      }
    }

    let framed = false;
    for (const { pattern, kind, slots, carriesArrow } of FRAME_PATTERNS) {
      const m = pattern.exec(rest);
      if (!m) continue;
      flush();
      /* The arrow eats group 1, so the slots start one later. */
      const first = carriesArrow ? 2 : 1;
      out.push({
        type: 'rframe',
        attrs: carriesArrow ? { kind, arrow: m[1]! } : { kind },
        content: Array.from({ length: slots }, (_, k) => docFromSource(m[first + k] ?? '')),
      });
      i += m[0].length;
      framed = true;
      break;
    }
    if (framed) continue;

    const scanned = SCAN.find(([code]) => source.startsWith(code, i));
    if (scanned) {
      const [code, isAtom] = scanned;
      if (isAtom) {
        flush();
        out.push({ type: 'rsym', attrs: { code }, ...(marks.length ? { marks } : {}) });
      }
      else plain += code;
      i += code.length;
      continue;
    }

    const lone = loneOperator(source, i);
    if (lone) {
      flush();
      out.push({ type: 'rsym', attrs: { code: lone } });
      i += lone.length;
      continue;
    }

    plain += source[i];
    i++;
  }
  flush();
  return out;
}

/**
 * mhchem as the EDITOR would show it: display text, with the palette's glyph for anything the
 * display table refuses.
 *
 * The same answer the field gives, from the same two sources, so a particle on a Feynman
 * diagram reads `e⁻` exactly as it does while being typed. Deriving it a third way is how
 * `e^-` ended up drawn with its caret showing: the palette's electron is `e^-{}` and a teacher
 * writes `e^-`, which is `e` followed by the `^-` atom.
 */
export function displayText(source: string): string {
  return docFromSource(source).map(n => {
    if (n.type === 'text') return n.text;
    if (n.type === 'rsym') return GLYPH_FOR_CODE.get(n.attrs.code) ?? toDisplay(n.attrs.code);
    if (n.type === 'rstructure') return n.attrs.name;
    return '';
  }).join('');
}
